// src/pages/admin/TeamDetail.jsx
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import {
    FaArrowLeft,
    FaEdit,
    FaSearch,
    FaPlus,
    FaPrint,
    FaSpinner,
    FaTimes,
    FaBuilding
} from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    pdf
} from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import defaultLogo from '../../assets/logo.png';
import { AppContext } from '../../context/AppContext';

// --- KONFIGURASI URL ---
const BACKEND_URL = process.env.REACT_APP_BACKEND || '';
const BASE_URL_PHOTO = `${BACKEND_URL}/uploads/athletes/`;
const UPLOAD_URL = `${BACKEND_URL}/uploads/settings/`;
const PREVIEW_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const PDF_STATE_RENDER_DELAY = 60;

const waitForUiUpdate = () => new Promise(resolve => {
    window.setTimeout(resolve, PDF_STATE_RENDER_DELAY);
});

const getUploadProxyUrl = (filePath, timestamp) => {
    return `${BACKEND_URL}/api/uploads/proxy?file=${encodeURIComponent(filePath)}&t=${timestamp}`;
};

const getAvatarUrl = (displayName) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random`;
};

// ==========================================
// Helper Data Atlet / Entry
// ==========================================
const normalizeText = (value) => String(value || '').toLowerCase().trim();

const getEntryMembers = (athlete) => {
    if (!athlete) return [];

    if (Array.isArray(athlete.entry_members)) {
        return athlete.entry_members
            .map(member => String(member || '').trim())
            .filter(Boolean);
    }

    if (typeof athlete.entry_members === 'string' && athlete.entry_members.trim() !== '') {
        try {
            const parsed = JSON.parse(athlete.entry_members);

            if (Array.isArray(parsed)) {
                return parsed
                    .map(member => String(member || '').trim())
                    .filter(Boolean);
            }
        } catch (error) {
            return athlete.entry_members
                .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                .map(member => String(member || '').trim())
                .filter(Boolean);
        }
    }

    return [];
};

const getDisplayName = (athlete) => {
    const members = getEntryMembers(athlete);

    if (members.length > 1) {
        return members.join(' / ');
    }

    return athlete?.name || 'NAMA ATLET';
};

const getTeamName = (team) => {
    return team?.name || '-';
};

const getManagerName = (team) => {
    return team?.manager_name || team?.user?.name || team?.User?.name || '-';
};

const getManagerPhone = (team) => {
    return team?.manager_phone || team?.user?.phone || team?.User?.phone || '';
};

const getManagerEmail = (team) => {
    return team?.user?.email || team?.User?.email || '';
};

const getGenderLabel = (gender) => {
    const value = normalizeText(gender);

    if (value === 'lelaki' || value === 'l' || value === 'putra') return 'Putra';
    if (value === 'perempuan' || value === 'p' || value === 'putri') return 'Putri';
    if (value === 'campuran' || value === 'mixed' || value === 'mix') return 'Campuran';

    return gender || '-';
};

const getAthleteClassText = (athlete) => {
    if (!athlete) return '-';

    if (athlete.competition_type === 'Kyorugi') {
        const weightClass = athlete.weight_class || '-';
        const belt = athlete.belt ? ` • Sabuk ${athlete.belt}` : '';

        return `${weightClass}${belt}`;
    }

    return athlete.poomsae_category || '-';
};

const getAthleteBadgeText = (athlete) => {
    const competitionType = athlete?.competition_type || '-';
    const type = athlete?.type || '';

    return `${competitionType} ${type}`.trim();
};

const getAthleteTypeBadgeColor = (athlete, config) => {
    if (athlete?.competition_type === 'Kyorugi') {
        return config?.badges?.kyorugiBg || '#ea580c';
    }

    return config?.badges?.poomsaeBg || '#7c3aed';
};

const sanitizeFileName = (value) => {
    return String(value || 'file')
        .replace(/[\\/:*?"<>|]/g, '-')
        .replace(/\s+/g, '_')
        .trim();
};

// ==========================================
// Helper: fetch image -> dataURL
// ==========================================
async function toDataUrl(url) {
    try {
        const res = await fetch(url, { mode: 'cors' });

        if (!res.ok) {
            throw new Error(`Failed to fetch (${res.status})`);
        }

        const blob = await res.blob();

        return await new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onerror = reject;
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    } catch (err) {
        console.warn('toDataUrl error for', url, err);
        return null;
    }
}

// ==========================================
// 1. GENERATOR STYLE PDF DINAMIS
// ==========================================
const createPdfStyles = (config) => StyleSheet.create({
    page: {
        width: config?.page?.width || 255,
        height: config?.page?.height || 340,
        backgroundColor: config?.page?.backgroundColor || '#ffffff',
        padding: 0,
        position: 'relative'
    },
    bgImage: {
        position: 'absolute',
        minWidth: '100%',
        minHeight: '100%',
        display: 'block',
        height: '100%',
        width: '100%',
        zIndex: -1
    },
    header: {
        height: config?.header?.height || 70,
        backgroundColor: config?.header?.backgroundColor || '#1e293b',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        paddingTop: config?.header?.paddingTop || 10,
        paddingHorizontal: 12
    },
    logoImage: {
        width: config?.logo?.width || 40,
        height: config?.logo?.width || 40,
        marginTop: config?.logo?.marginTop || 0,
        objectFit: 'contain'
    },
    eventTextContainer: {
        width: config?.event_title?.width || '60%',
        alignItems:
            config?.event_title?.textAlign === 'left'
                ? 'flex-start'
                : config?.event_title?.textAlign === 'center'
                    ? 'center'
                    : 'flex-end'
    },
    eventText: {
        color: config?.event_title?.color || '#fbbf24',
        fontSize: config?.event_title?.fontSize || 7,
        fontWeight: 'bold',
        letterSpacing: 0.5,
        textAlign: config?.event_title?.textAlign || 'right',
        marginTop: config?.event_title?.marginTop || 5
    },
    photoArea: {
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: config?.photo?.marginTop || -20
    },
    photo: {
        width: config?.photo?.width || 100,
        height: config?.photo?.height || 125,
        borderRadius: config?.photo?.borderRadius || 4,
        borderWidth: config?.photo?.borderWidth || 2,
        borderColor: config?.photo?.borderColor || 'white',
        backgroundColor: config?.photo?.backgroundColor || '#f3f4f6',
        objectFit: 'cover'
    },
    body: {
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginTop: 10,
        paddingHorizontal: 10
    },
    name: {
        fontSize: config?.athlete_name?.fontSize || 12,
        fontWeight: 'bold',
        color: config?.athlete_name?.color || '#0f172a',
        marginTop: config?.athlete_name?.marginTop || 10,
        marginBottom: config?.athlete_name?.marginBottom || 2,
        textAlign: 'center',
        textTransform: 'uppercase'
    },
    team: {
        fontSize: config?.team_name?.fontSize || 9,
        color: config?.team_name?.color || '#64748b',
        marginBottom: config?.team_name?.marginBottom || 8,
        fontWeight: 'medium',
        textAlign: 'center',
        textTransform: 'uppercase'
    },
    badgeContainer: {
        flexDirection: 'row',
        gap: 6,
        marginBottom: 8,
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap'
    },
    badge: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
        fontSize: config?.badges?.fontSize || 6,
        color: config?.badges?.textColor || 'white',
        fontWeight: 'bold',
        textTransform: 'uppercase'
    },
    division: {
        fontSize: 8,
        fontWeight: 'bold',
        color: '#334155'
    },
    entryBadge: {
        paddingHorizontal: 7,
        paddingVertical: 2,
        borderRadius: 10,
        fontSize: 5.5,
        color: '#0891b2',
        backgroundColor: '#ecfeff',
        fontWeight: 'bold',
        textTransform: 'uppercase'
    },
    details: {
        fontSize: config?.detail_text?.fontSize || 10,
        fontWeight: 'bold',
        borderTopWidth: 1,
        borderTopColor: config?.detail_text?.borderColor || '#cbd5e1',
        borderStyle: 'dashed',
        paddingTop: 6,
        marginTop: config?.detail_text?.marginTop || 5,
        width: '85%',
        textAlign: 'center',
        color: config?.detail_text?.color || '#0f172a'
    },
    footer: {
        height: config?.footer?.height || 40,
        backgroundColor: config?.footer?.backgroundColor || '#f8fafc',
        borderTopWidth: 1,
        borderTopColor: config?.footer?.borderTopColor || '#e2e8f0',
        alignItems: 'center',
        justifyContent: 'center'
    },
    footerMain: {
        fontSize: 7,
        fontWeight: 'bold',
        color: config?.footer?.mainTextColor || '#0f172a'
    },
    footerSub: {
        fontSize: 5,
        color: config?.footer?.subTextColor || '#64748b',
        marginTop: 2
    }
});

// ==========================================
// 2. KOMPONEN DOKUMEN PDF
// ==========================================
const IdCardDocument = ({ athletes, teamName, eventName, config, logoDataUrl, bgDataUrl }) => {
    const styles = createPdfStyles(config);
    const timestamp = new Date().getTime();

    const logoSource = config?.logo?.url
        ? (logoDataUrl || `${UPLOAD_URL}${config.logo.url}?t=${timestamp}`)
        : defaultLogo;

    const bgSource = config?.page?.backgroundImage
        ? (bgDataUrl || `${UPLOAD_URL}${config.page.backgroundImage}?t=${timestamp}`)
        : null;

    const cardTitle = config?.event_title?.text || eventName;

    return (
        <Document>
            {athletes.map((athlete, index) => {
                const displayName = getDisplayName(athlete);
                const members = getEntryMembers(athlete);
                const detailText = getAthleteClassText(athlete);

                const photoUrl = athlete.image
                    ? getUploadProxyUrl(`uploads/athletes/${athlete.image}`, timestamp)
                    : getAvatarUrl(displayName);

                return (
                    <Page key={index} size={[styles.page.width, styles.page.height]} style={styles.page}>
                        {bgSource && (
                            <Image
                                src={bgSource}
                                style={styles.bgImage}
                                fixed
                            />
                        )}

                        <View style={styles.header}>
                            {config?.logo?.url ? (
                                <Image src={logoSource} style={styles.logoImage} />
                            ) : (
                                <Image src={defaultLogo} style={styles.logoImage} />
                            )}

                            <View style={styles.eventTextContainer}>
                                <Text style={styles.eventText}>
                                    {String(cardTitle || eventName).toUpperCase()}
                                </Text>
                            </View>
                        </View>

                        <View style={styles.photoArea}>
                            <Image
                                src={photoUrl}
                                style={styles.photo}
                            />
                        </View>

                        <View style={styles.body}>
                            <Text style={styles.name}>{displayName}</Text>
                            <Text style={styles.team}>{teamName || 'TIM TIDAK DIKETAHUI'}</Text>

                            <View style={styles.badgeContainer}>
                                <View
                                    style={[
                                        styles.badge,
                                        {
                                            backgroundColor: getAthleteTypeBadgeColor(athlete, config)
                                        }
                                    ]}
                                >
                                    <Text>{getAthleteBadgeText(athlete)}</Text>
                                </View>

                                <Text style={styles.division}>
                                    {athlete.division || '-'}
                                </Text>

                                {members.length > 1 && (
                                    <View style={styles.entryBadge}>
                                        <Text>{members.length} NAMA / 1 ENTRY</Text>
                                    </View>
                                )}
                            </View>

                            <Text style={styles.details}>
                                {detailText}
                            </Text>
                        </View>
                    </Page>
                );
            })}
        </Document>
    );
};

// ==========================================
// 3. KOMPONEN UTAMA
// ==========================================
const TeamDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { settings, idCardConfig } = useContext(AppContext);

    const [team, setTeam] = useState(null);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterGender, setFilterGender] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [previewPage, setPreviewPage] = useState(1);
    const [previewPageSize, setPreviewPageSize] = useState(25);

    const [showModal, setShowModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);

    const [editFormData, setEditFormData] = useState({
        name: '',
        manager_name: '',
        email: '',
        phone: ''
    });

    const [logoDataUrl, setLogoDataUrl] = useState(null);
    const [bgDataUrl, setBgDataUrl] = useState(null);
    const [isPreloading, setIsPreloading] = useState(false);
    const [pdfLoadingKey, setPdfLoadingKey] = useState(null);

    const eventName = settings?.event_name || "KEJUARAAN TAEKWONDO";
    const cfg = idCardConfig || {};
    const previewAssetVersion = encodeURIComponent([
        cfg.logo?.url || 'default-logo',
        cfg.page?.backgroundImage || 'default-bg'
    ].join('|'));

    const fetchTeamDetail = async () => {
        setLoading(true);

        try {
            const response = await api.get(`/teams/${id}?t=${new Date().getTime()}`);
            const data = response.data.result || response.data.data || response.data;

            setTeam(data);
        } catch (error) {
            toast.error("Gagal memuat detail tim");
            navigate('/admin/tim');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeamDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, location.key]);

    useEffect(() => {
        let mounted = true;

        const preload = async () => {
            setIsPreloading(true);

            const timestamp = new Date().getTime();

            try {
                const logoUrl = idCardConfig?.logo?.url
                    ? getUploadProxyUrl(`uploads/settings/${idCardConfig.logo.url}`, timestamp)
                    : null;

                const bgUrl = idCardConfig?.page?.backgroundImage
                    ? getUploadProxyUrl(`uploads/settings/${idCardConfig.page.backgroundImage}`, timestamp)
                    : null;

                if (logoUrl) {
                    const logo = await toDataUrl(logoUrl);
                    if (mounted) setLogoDataUrl(logo);
                } else if (mounted) {
                    setLogoDataUrl(null);
                }

                if (bgUrl) {
                    const bg = await toDataUrl(bgUrl);
                    if (mounted) setBgDataUrl(bg);
                } else if (mounted) {
                    setBgDataUrl(null);
                }
            } catch (err) {
                console.warn('Preload error', err);

                if (mounted) {
                    setLogoDataUrl(null);
                    setBgDataUrl(null);
                }
            } finally {
                if (mounted) setIsPreloading(false);
            }
        };

        preload();

        return () => {
            mounted = false;
        };
    }, [idCardConfig]);

    const handleOpenEditModal = () => {
        if (!team) return;

        setEditFormData({
            name: team.name || '',
            manager_name: getManagerName(team),
            email: getManagerEmail(team),
            phone: getManagerPhone(team)
        });

        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleEditChange = (e) => {
        setEditFormData({
            ...editFormData,
            [e.target.name]: e.target.value
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        setModalLoading(true);

        try {
            await api.put(`/teams/${id}`, editFormData);

            toast.success("Informasi tim berhasil diperbarui!");
            setShowModal(false);
            fetchTeamDetail();
        } catch (error) {
            toast.error(error.response?.data?.msg || "Gagal mengupdate data tim");
        } finally {
            setModalLoading(false);
        }
    };

    const processedAthletes = useMemo(() => {
        if (!team || !Array.isArray(team.athletes)) return [];

        let result = [...team.athletes];

        if (searchTerm) {
            const keyword = normalizeText(searchTerm);

            result = result.filter(athlete => {
                const members = getEntryMembers(athlete).join(' ');
                const searchableText = [
                    athlete.name,
                    getDisplayName(athlete),
                    members,
                    athlete.competition_type,
                    athlete.type,
                    athlete.gender,
                    athlete.division,
                    athlete.weight_class,
                    athlete.poomsae_category,
                    athlete.belt
                ].join(' ');

                return normalizeText(searchableText).includes(keyword);
            });
        }

        if (filterGender) {
            result = result.filter(athlete => {
                const gender = normalizeText(athlete.gender);

                if (filterGender === 'L') {
                    return gender === 'lelaki' || gender === 'l' || gender === 'putra';
                }

                if (filterGender === 'P') {
                    return gender === 'perempuan' || gender === 'p' || gender === 'putri';
                }

                if (filterGender === 'C') {
                    return gender === 'campuran' || gender === 'mixed' || gender === 'mix';
                }

                return true;
            });
        }

        if (filterCategory) {
            result = result.filter(athlete => athlete.competition_type === filterCategory);
        }

        result.sort((a, b) => {
            const nameA = getDisplayName(a).toLowerCase();
            const nameB = getDisplayName(b).toLowerCase();

            return sortOrder === 'asc'
                ? nameA.localeCompare(nameB)
                : nameB.localeCompare(nameA);
        });

        return result;
    }, [team, searchTerm, filterGender, filterCategory, sortOrder]);

    const totalPreviewPages = Math.max(1, Math.ceil(processedAthletes.length / previewPageSize));
    const safePreviewPage = Math.min(previewPage, totalPreviewPages);
    const previewStartIndex = processedAthletes.length === 0
        ? 0
        : (safePreviewPage - 1) * previewPageSize + 1;
    const previewEndIndex = Math.min(safePreviewPage * previewPageSize, processedAthletes.length);

    const paginatedAthletes = useMemo(() => {
        const start = (safePreviewPage - 1) * previewPageSize;
        return processedAthletes.slice(start, start + previewPageSize);
    }, [processedAthletes, safePreviewPage, previewPageSize]);

    useEffect(() => {
        setPreviewPage(1);
    }, [searchTerm, filterGender, filterCategory, sortOrder, id]);

    useEffect(() => {
        if (previewPage > totalPreviewPages) {
            setPreviewPage(totalPreviewPages);
        }
    }, [previewPage, totalPreviewPages]);

    const downloadIdCardPdf = async ({ athletes, fileName, loadingKey }) => {
        if (!athletes.length) {
            toast.warning("Tidak ada data ID card untuk dicetak.");
            return;
        }

        if (pdfLoadingKey || isPreloading) return;

        const toastId = toast.loading(
            athletes.length > 1
                ? `Membuat PDF ${athletes.length} ID card...`
                : "Membuat PDF ID card..."
        );

        setPdfLoadingKey(loadingKey);

        try {
            await waitForUiUpdate();

            const blob = await pdf(
                <IdCardDocument
                    athletes={athletes}
                    teamName={getTeamName(team)}
                    eventName={eventName}
                    config={idCardConfig}
                    logoDataUrl={logoDataUrl}
                    bgDataUrl={bgDataUrl}
                />
            ).toBlob();

            saveAs(blob, fileName);
            toast.dismiss(toastId);
            toast.success("PDF ID card berhasil dibuat.");
        } catch (error) {
            console.error("Gagal membuat PDF ID card:", error);
            toast.dismiss(toastId);
            toast.error("Gagal membuat PDF ID card.");
        } finally {
            setPdfLoadingKey(null);
        }
    };

    const handleDownloadAllIdCards = () => {
        downloadIdCardPdf({
            athletes: processedAthletes,
            fileName: `ID_Card_${sanitizeFileName(getTeamName(team))}.pdf`,
            loadingKey: 'all'
        });
    };

    const handleDownloadSingleIdCard = (athlete, displayName) => {
        const loadingKey = `athlete-${athlete.id || athlete.uuid}`;

        downloadIdCardPdf({
            athletes: [athlete],
            fileName: `ID_${sanitizeFileName(displayName)}.pdf`,
            loadingKey
        });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary"></div>
            </div>
        );
    }

    if (!team) return null;

    const teamName = getTeamName(team);
    const managerName = getManagerName(team);

    const logoPreview = cfg.logo?.url
        ? `${UPLOAD_URL}${cfg.logo.url}?t=${previewAssetVersion}`
        : defaultLogo;

    const bgPreview = cfg.page?.backgroundImage
        ? `${UPLOAD_URL}${cfg.page.backgroundImage}?t=${previewAssetVersion}`
        : null;

    return (
        <div className="container-fluid p-0 animate-fade-in">
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4 gap-3">
                <div className="d-flex align-items-center gap-3">
                    <Link
                        to="/admin/tim"
                        className="btn btn-light bg-white shadow-sm border rounded-circle d-flex align-items-center justify-content-center hover-scale p-0"
                        style={{ width: '42px', height: '42px' }}
                    >
                        <FaArrowLeft className="text-muted" />
                    </Link>

                    <div>
                        <h4 className="fw-bold text-dark mb-1">{teamName}</h4>
                        <p className="text-muted m-0 small">
                            Manajer: <span className="fw-bold text-primary">{managerName}</span>
                        </p>
                    </div>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                    <button
                        onClick={handleOpenEditModal}
                        className="btn btn-white border shadow-sm rounded-pill px-4 fw-bold text-primary hover-scale d-flex align-items-center gap-2"
                    >
                        <FaEdit /> Edit Tim
                    </button>

                    <button
                        type="button"
                        onClick={handleDownloadAllIdCards}
                        disabled={isPreloading || Boolean(pdfLoadingKey) || processedAthletes.length === 0}
                        className="btn btn-primary shadow-sm rounded-pill px-4 d-flex align-items-center gap-2 hover-scale"
                    >
                        {(pdfLoadingKey === 'all' || isPreloading)
                            ? <><FaSpinner className="fa-spin" /> Menyiapkan...</>
                            : <><FaPrint /> Cetak Semua ID Card</>
                        }
                    </button>
                </div>
            </div>

            {/* FILTER BAR */}
            <div className="card border-0 shadow-sm mb-4 rounded-4 bg-white">
                <div className="card-body p-4">
                    <div className="row g-3 align-items-center">
                        <div className="col-lg-5">
                            <div className="input-group shadow-sm rounded-pill overflow-hidden">
                                <span className="input-group-text bg-light border-0 ps-4">
                                    <FaSearch className="text-muted" />
                                </span>

                                <input
                                    type="text"
                                    className="form-control bg-light border-0 py-2"
                                    placeholder="Cari nama atlet / entry / sabuk / kelas..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="col-md-3">
                            <select
                                className="form-select bg-light border-0 shadow-sm rounded-pill py-2 text-muted fw-medium px-4"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                            >
                                <option value="">Semua Kategori</option>
                                <option value="Kyorugi">Kyorugi</option>
                                <option value="Poomsae">Poomsae</option>
                            </select>
                        </div>

                        <div className="col-md-2">
                            <select
                                className="form-select bg-light border-0 shadow-sm rounded-pill py-2 text-muted fw-medium px-4"
                                value={filterGender}
                                onChange={(e) => setFilterGender(e.target.value)}
                            >
                                <option value="">Gender</option>
                                <option value="L">Putra</option>
                                <option value="P">Putri</option>
                                <option value="C">Campuran</option>
                            </select>
                        </div>

                        <div className="col-md-2">
                            <button
                                onClick={() => navigate('/admin/atlet/tambah', {
                                    state: {
                                        preselectedTeamId: team.id,
                                        preselectedTeamName: teamName
                                    }
                                })}
                                className="btn py-2 btn-success w-100 rounded-pill shadow-sm fw-bold hover-scale d-flex justify-content-center align-items-center gap-2"
                            >
                                <FaPlus /> Tambah
                            </button>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                        <div className="small text-muted fw-bold">
                            Menampilkan {processedAthletes.length} dari {team.athletes?.length || 0} entry.
                        </div>

                        <button
                            type="button"
                            className="btn btn-sm btn-light border rounded-pill fw-bold px-3"
                            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                        >
                            Urut Nama: {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
                        </button>
                    </div>
                </div>
            </div>

            {/* HTML PREVIEW GRID */}
            <div className="id-card-grid">
                {processedAthletes.length > 0 ? (
                    paginatedAthletes.map((athlete) => {
                        const displayName = getDisplayName(athlete);
                        const members = getEntryMembers(athlete);
                        const detailText = getAthleteClassText(athlete);
                        const athletePrintKey = `athlete-${athlete.id || athlete.uuid}`;

                        return (
                            <div className="id-card-item" key={athlete.id || athlete.uuid}>
                                <div
                                    className="id-card-preview shadow-sm"
                                    style={{
                                        width: '240px',
                                        height: '320px',
                                        backgroundColor: cfg.page?.backgroundColor || '#fff',
                                        position: 'relative',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '16px',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    {bgPreview && (
                                        <img
                                            src={bgPreview}
                                            alt=""
                                            loading="lazy"
                                            className="position-absolute w-100 h-100 object-fit-cover"
                                            style={{ zIndex: 0, opacity: 1 }}
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    )}

                                    {/* Header */}
                                    <div
                                        style={{
                                            height: `${cfg.header?.height || 70}px`,
                                            backgroundColor: cfg.header?.backgroundColor || '#1e293b',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: cfg.header?.alignItems || 'flex-start',
                                            justifyContent: 'space-between',
                                            paddingTop: `${cfg.header?.paddingTop || 10}px`,
                                            paddingLeft: '12px',
                                            paddingRight: '12px',
                                            zIndex: 1
                                        }}
                                    >
                                        {cfg.logo?.show !== false ? (
                                            <img
                                                src={logoPreview}
                                                alt="Logo"
                                                loading="lazy"
                                                style={{
                                                    width: `${cfg.logo?.width || 40}px`,
                                                    height: `${cfg.logo?.width || 40}px`,
                                                    marginTop: `${cfg.logo?.marginTop || 0}px`,
                                                    objectFit: 'contain'
                                                }}
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        ) : (
                                            <div style={{ width: 40 }}></div>
                                        )}

                                        <div style={{ width: cfg.event_title?.width || '60%', textAlign: cfg.event_title?.textAlign || 'right' }}>
                                            <div
                                                style={{
                                                    color: cfg.event_title?.color || '#fbbf24',
                                                    fontSize: '10px',
                                                    fontWeight: 'bold',
                                                    marginTop: `${cfg.event_title?.marginTop || 5}px`,
                                                    lineHeight: 1.1
                                                }}
                                            >
                                                {cfg.event_title?.text || eventName}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Photo */}
                                    <div
                                        className="d-flex justify-content-center position-relative"
                                        style={{
                                            zIndex: 2,
                                            marginTop: `${cfg.photo?.marginTop || -20}px`
                                        }}
                                    >
                                        <img
                                            src={
                                                athlete.image
                                                    ? `${BASE_URL_PHOTO}${athlete.image}`
                                                    : getAvatarUrl(displayName)
                                            }
                                            alt={displayName}
                                            loading="lazy"
                                            style={{
                                                width: `${cfg.photo?.width || 100}px`,
                                                height: `${cfg.photo?.height || 125}px`,
                                                border: `${cfg.photo?.borderWidth || 2}px solid ${cfg.photo?.borderColor || '#fff'}`,
                                                borderRadius: `${cfg.photo?.borderRadius || 4}px`,
                                                backgroundColor: cfg.photo?.backgroundColor || '#f3f4f6',
                                                objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                                e.target.src = getAvatarUrl(displayName);
                                            }}
                                        />
                                    </div>

                                    {/* Body */}
                                    <div
                                        className="flex-grow-1 px-3 text-center position-relative d-flex flex-column justify-content-start pt-2"
                                        style={{ zIndex: 1 }}
                                    >
                                        <div
                                            className="id-card-name"
                                            style={{
                                                color: cfg.athlete_name?.color || '#0f172a',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                textTransform: 'uppercase',
                                                marginTop: `${cfg.athlete_name?.marginTop || 10}px`
                                            }}
                                        >
                                            {displayName}
                                        </div>

                                        <div
                                            style={{
                                                color: cfg.team_name?.color || '#64748b',
                                                fontSize: '9px',
                                                fontWeight: 'normal',
                                                textTransform: 'uppercase',
                                                marginBottom: '5px'
                                            }}
                                        >
                                            {teamName}
                                        </div>

                                        <div className="d-flex justify-content-center gap-1 mb-1 align-items-center flex-wrap">
                                            <span
                                                style={{
                                                    backgroundColor: getAthleteTypeBadgeColor(athlete, cfg),
                                                    color: cfg.badges?.textColor || 'white',
                                                    fontSize: '8px',
                                                    padding: '2px 8px',
                                                    borderRadius: '10px',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {getAthleteBadgeText(athlete)}
                                            </span>

                                            <span
                                                style={{
                                                    fontSize: '9px',
                                                    fontWeight: 'bold',
                                                    color: '#334155'
                                                }}
                                            >
                                                {athlete.division || '-'}
                                            </span>

                                            {members.length > 1 && (
                                                <span
                                                    style={{
                                                        backgroundColor: '#ecfeff',
                                                        color: '#0891b2',
                                                        border: '1px solid #a5f3fc',
                                                        fontSize: '7px',
                                                        padding: '2px 7px',
                                                        borderRadius: '10px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {members.length} NAMA / 1 ENTRY
                                                </span>
                                            )}
                                        </div>

                                        <div
                                            style={{
                                                color: cfg.detail_text?.color || '#1e293b',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                borderTop: `1px dashed ${cfg.detail_text?.borderColor || '#cbd5e1'}`,
                                                paddingTop: '6px',
                                                marginTop: `${cfg.detail_text?.marginTop || 5}px`,
                                                width: '85%',
                                                alignSelf: 'center'
                                            }}
                                        >
                                            {detailText}
                                        </div>

                                        <div className="small text-muted mt-2">
                                            {getGenderLabel(athlete.gender)}
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="d-flex justify-content-center gap-2 mt-3">
                                    <button
                                        type="button"
                                        onClick={() => handleDownloadSingleIdCard(athlete, displayName)}
                                        disabled={isPreloading || Boolean(pdfLoadingKey)}
                                        className="btn btn-light bg-white border text-dark hover-scale rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0"
                                        style={{ width: '35px', height: '35px' }}
                                        title="Cetak ID Card Ini"
                                    >
                                        {(pdfLoadingKey === athletePrintKey || isPreloading)
                                            ? <FaSpinner className="fa-spin" />
                                            : <FaPrint />
                                        }
                                    </button>

                                    <Link
                                        to={`/admin/atlet/edit/${athlete.uuid}`}
                                        className="btn btn-light bg-white border text-primary hover-scale rounded-circle shadow-sm d-flex align-items-center justify-content-center p-0"
                                        style={{ width: '35px', height: '35px' }}
                                        title="Edit Profil Atlet"
                                    >
                                        <FaEdit />
                                    </Link>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="col-12 text-center py-5">
                        <div className="text-muted opacity-50 mb-3">
                            <FaBuilding size={50} />
                        </div>
                        <h6 className="text-muted fw-bold">Belum ada atlet / entry terdaftar di tim ini.</h6>
                    </div>
                )}
            </div>

            {processedAthletes.length > 0 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 py-3 mb-4 border-top">
                    <div className="small text-muted fw-bold">
                        Preview {previewStartIndex}-{previewEndIndex} dari {processedAthletes.length} ID card
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <select
                            className="form-select form-select-sm bg-white border shadow-sm rounded-pill w-auto fw-bold text-muted"
                            value={previewPageSize}
                            onChange={(e) => {
                                setPreviewPageSize(Number(e.target.value));
                                setPreviewPage(1);
                            }}
                            aria-label="Jumlah preview ID card per halaman"
                        >
                            {PREVIEW_PAGE_SIZE_OPTIONS.map(option => (
                                <option key={option} value={option}>
                                    {option} / halaman
                                </option>
                            ))}
                        </select>

                        <nav aria-label="Pagination preview ID card">
                            <ul className="pagination pagination-sm mb-0 gap-1 shadow-sm rounded">
                                <li className={`page-item ${safePreviewPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        type="button"
                                        className="page-link border-0 text-dark fw-bold bg-light"
                                        onClick={() => setPreviewPage(prev => Math.max(1, prev - 1))}
                                        disabled={safePreviewPage === 1}
                                    >
                                        Prev
                                    </button>
                                </li>

                                <li className="page-item active">
                                    <button type="button" className="page-link border-0 bg-primary fw-bold px-3">
                                        {safePreviewPage}
                                    </button>
                                </li>

                                <li className={`page-item ${safePreviewPage >= totalPreviewPages ? 'disabled' : ''}`}>
                                    <button
                                        type="button"
                                        className="page-link border-0 text-dark fw-bold bg-light"
                                        onClick={() => setPreviewPage(prev => Math.min(totalPreviewPages, prev + 1))}
                                        disabled={safePreviewPage >= totalPreviewPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>

                        <span className="small text-muted fw-bold">
                            Halaman {safePreviewPage} dari {totalPreviewPages}
                        </span>
                    </div>
                </div>
            )}

            {/* MODAL EDIT TIM */}
            {showModal && createPortal(
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                        zIndex: 1060,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(3px)'
                    }}
                >
                    <div
                        className="card border-0 shadow-lg rounded-4 p-0 overflow-hidden animate-pop-in"
                        style={{
                            width: '90%',
                            maxWidth: '500px'
                        }}
                    >
                        <div className="card-header bg-white border-bottom-0 d-flex justify-content-between align-items-center pt-4 px-4">
                            <h5 className="fw-bold m-0 text-dark d-flex align-items-center gap-2">
                                <FaBuilding className="text-primary" /> Edit Data Tim
                            </h5>

                            <button
                                onClick={handleCloseModal}
                                className="btn btn-sm btn-light rounded-circle hover-scale text-danger d-flex align-items-center justify-content-center"
                                style={{
                                    width: '32px',
                                    height: '32px'
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="card-body p-4 pt-2">
                            <form onSubmit={handleEditSubmit}>
                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-muted">Nama Tim / Dojang</label>
                                    <input
                                        type="text"
                                        name="name"
                                        className="form-control bg-light border-0 py-2"
                                        required
                                        value={editFormData.name}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-muted">Nama Manajer</label>
                                    <input
                                        type="text"
                                        name="manager_name"
                                        className="form-control bg-light border-0 py-2"
                                        value={editFormData.manager_name}
                                        onChange={handleEditChange}
                                    />
                                </div>

                                <div className="row g-3 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold text-muted">Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control bg-light border-0 py-2"
                                            value={editFormData.email}
                                            onChange={handleEditChange}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label small fw-bold text-muted">WhatsApp</label>
                                        <input
                                            type="text"
                                            name="phone"
                                            className="form-control bg-light border-0 py-2"
                                            value={editFormData.phone}
                                            onChange={handleEditChange}
                                        />
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end gap-2">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="btn btn-light fw-bold rounded-pill px-4 hover-scale"
                                    >
                                        Batal
                                    </button>

                                    <button
                                        type="submit"
                                        className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm hover-scale"
                                        disabled={modalLoading}
                                    >
                                        {modalLoading
                                            ? <><FaSpinner className="fa-spin me-2" /> Menyimpan...</>
                                            : 'Simpan Perubahan'
                                        }
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* CSS Internal */}
            <style>{`
                .animate-fade-in {
                    animation: fadeIn 0.4s ease-out forwards;
                }

                .animate-pop-in {
                    animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
                }

                .hover-scale {
                    transition: transform 0.2s;
                }

                .hover-scale:hover {
                    transform: scale(1.05);
                }

                .id-card-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
                    gap: 30px;
                    padding-bottom: 40px;
                }

                .id-card-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .id-card-preview {
                    transition: all 0.3s ease;
                }

                .id-card-preview:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 25px rgba(0,0,0,0.1) !important;
                }

                .id-card-name {
                    max-width: 100%;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    line-height: 1.15;
                    word-break: break-word;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes popIn {
                    0% {
                        transform: scale(0.9);
                        opacity: 0;
                    }
                    100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
};

export default TeamDetail;
